<template>
  <svg :id="id" ref="svgElement" class="spider-chart simcision-chart"></svg>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted, watch } from "vue";
import { v4 } from "uuid";
import { scaleLinear } from "d3-scale";
import { select } from "d3-selection";
import { line } from "d3-shape";
import { axisRight } from "d3-axis";
import { easeExpOut } from "d3-ease";
import { useI18n } from "vue-i18n";
import { mapNumber } from "@/helpers/utility/MathHelper";

import { selectOrCreateChild, wrapText } from "./chartDrawFunction";
import { useChartEngine } from "./useChartEngine";

import type { ScaleLinear } from "d3-scale";
import type { DataEntry, EntryValue } from "./ChartData";
import type { ChartComponentProps, ChartEmitEvents } from "./useChartEngine";

const { t } = useI18n();

const props = withDefaults(defineProps<ChartComponentProps>(), {
  id: () => v4(),
  yUnit: "%",
  width: 960,
  height: 500,
  settings: () => ({}),
  formattedTitle: () => [],
  animationsDisabled: false,
  titleClickable: false,
  margin: () => ({ top: 20, left: 20, bottom: 20, right: 20 })
});

const emit = defineEmits<ChartEmitEvents>();

// SETUP
const ctx = useChartEngine(() => props, { emit });
const chart = reactive(ctx);
// @ts-expect-error - svgElement is used as template ref
const { svgElement } = ctx; // Extracted for template ref binding (used in <svg ref="svgElement">)

const scale = ref<ScaleLinear<number, number, never>>();

const minDimension = computed(() => {
  return Math.min(chart.innerHeight, chart.innerWidth);
});
const chartRadius = computed(() => {
  return minDimension.value / 2;
});
const axisCount = computed(() => {
  return Math.max(chart.longestValuesArray.length, 3);
});
const tickAmount = computed(() => {
  return Math.min(
    Math.max(Math.floor(mapNumber(chartRadius.value, 0, 400, 3, 10)), 3),
    10
  );
});
const longestLabelWidth = computed(() => {
  return chart.longestValuesArray.reduce((longest, d) => {
    const t = props.settings.xAxisLabelsShortened ? d.shortTitle : d.title;

    // save the longest width
    const width = chart.measureTextWidthCached(t, props.settings.fontSize || 12);
    return Math.max(longest, width);
  }, 0);
});

watch([() => props.width, () => props.height], ([width, height]) => {
  if (!chart.svg) return;
  chart.svg.attr("width", width);
  chart.svg.attr("height", height);
  chart.svg
    .select(".chart__background")
    .attr("width", width)
    .attr("height", height);

  updateChart();
});

watch(
  [() => props.data, () => props.settings],
  () => {
    updateChart();
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  createChart();

  if (!props.animationsDisabled) {
    setTimeout(() => {
      chart.animationDuration = 500;
    }, 200);
  }
});

/**
 * Setup the chart.
 * This needs to be called before any update functions.
 */
function createChart() {
  if (!chart.svgElement) return;

  // Connect the SVG
  const svg = select<SVGSVGElement, unknown>(chart.svgElement) // so I connected svg element in Template with this svg reference
    .attr("width", props.width)
    .attr("height", props.height);

  // Remove the container if it exists already
  svg.select(".chart__container").remove();

  // Create the background rect
  // This is relevant for SVG exports.
  if (props.settings?.backgroundFilled !== false) {
    svg
      .append("rect")
      .attr("width", props.width)
      .attr("height", props.height)
      .attr("class", "chart__background");
  }

  // (Re-)Create the container
  const g = svg.append("g").attr("class", "chart__container");

  const header = svg.append("g").attr("class", "chart__header");
  header.append("rect").attr("class", "chart__header-bg");
  header.append("text").attr("class", "chart__title");
  // create the axis containers
  g.append("g").attr("class", "spider-chart__areas");
  g.append("g").attr("class", "gx");
  g.append("g").attr("class", "gy");
  g.append("g").attr("class", "spider-chart__labels");
  g.append("g").attr("class", "spider-chart__points");

  // save the svg and container to the component data.
  chart.svg = svg;
  chart.container = g;

  updateChart();
}

/**
 * Update the SVG: change it to represent the
 * current state of the data array.
 */
function updateChart() {
  chart.labelsHeight = (props.settings.fontSize || 12) * 5.85;
  positionContainer();
  chart.drawTitle();
  updateScales();
  drawAreas();
  drawAxes();
  drawTicks();
  drawScale();
  drawLabels();
  drawPoints();

  drawYAxisLabel();
}

function positionContainer() {
  const g = chart.container;
  if (!g) return;

  if (!props.settings.xAxisLabelsVisible) {
    chart.labelsWidth = 0;
  } else {
    chart.labelsWidth = Math.min(longestLabelWidth.value, 200);
  }
  chart.paddingLeft = chart.labelsWidth + 10;
  chart.paddingRight = chart.labelsWidth + 10;

  g.attr(
    "transform",
    `translate(${props.margin.left + chart.paddingLeft}, ${
      props.margin.top + chart.labelsHeight / 2 + chart.titleHeight
    })`
  );
}

/**
 * Update xScale and yScale, depending on the data and the chart dimensions.
 */
function updateScales() {
  updateYScale();
}
function updateYScale() {
  scale.value = scaleLinear()
    .domain(chart.domain)
    .range([0, chartRadius.value]);
}

function drawScale() {
  if (chart.container == null) return;
  if (axisCount.value == 0) return;
  if (scale.value == null) return;

  const g = chart.container.select(".gy");

  if (!props.settings.yAxisLabelsVisible) {
    g.select(".chart__y-ticks").remove();
    return;
  }

  // Use scaled axis if we're scaling values.
  const scaleCopy = scale.value.copy();

  const drawAxis = axisRight(scaleCopy).ticks(tickAmount.value);

  if (g.select(".chart__y-ticks").empty()) {
    g.append("g").attr("class", "chart__y-ticks");
  }

  const axis = g
    .select<SVGGElement>(".chart__y-ticks")
    .classed("-overlaying", true)
    .transition()
    .duration(chart.animationDuration)
    .call(drawAxis);
  
  const axisFontSize = (props.settings.fontSize || 12) / 12 * 11;
  axis.selectAll("text").style("font-size", `${axisFontSize}px`);

  return axis;
}

function drawAreas() {
  if (chart.container == null) return;
  if (chart.filteredData.length == 0) return;
  if (scale.value == null) return;
  const data = chart.filteredData;

  const domainMax = scale.value.domain()[1];

  chart.container
    .select(".spider-chart__areas")
    .attr(
      "transform",
      `translate(${chart.innerWidth / 2}, ${chart.innerHeight / 2})`
    );

  if (chart.container.select(".spider-chart__backgroundarea").empty()) {
    chart.container
      .select(".spider-chart__areas")
      .append("path")
      .attr("class", "spider-chart__backgroundarea");
  }
  chart.container
    .select(".spider-chart__backgroundarea")
    .attr(
      "d",
      line()(
        new Array(axisCount.value)
          .fill(null)
          .map((v, i) => [getCoordX(i, domainMax), getCoordY(i, domainMax)])
      )
    );

  chart.container
    .select(".spider-chart__areas")
    .selectAll<SVGElement, DataEntry>(".spider-chart__area")
    .data(data, (d) => d.key)
    .join(
      (enter) =>
        enter
          .append("path")
          .attr("class", "spider-chart__area")
          .attr("d", (d) => {
            const description = line()(
              d.values.map((v, i) => [
                getCoordX(i, v.startValue || 0, 0),
                getCoordY(i, v.startValue || 0, 0)
              ])
            );
            if (description) return description + " Z";
            return description;
          }),
      (update) => update,
      (exit) => exit.remove()
    )
    .transition()
    .duration(chart.animationDuration)
    .attr("d", (d) => {
      const description = line()(
        d.values.map((v, i) => [
          getCoordX(i, v.value || 0, 0),
          getCoordY(i, v.value || 0, 0)
        ])
      );
      if (description) return description + " Z";
      return description;
    })
    .attr("fill", (d) =>
      props.settings.areasFilled ? d.color || "var(--color-text)" : "none"
    )
    .attr("stroke", (d) => d.color || "var(--color-text)");
}

function drawPoints() {
  if (chart.container == null) return;
  if (chart.filteredData.length == 0) return;
  const data = props.settings.endPointsVisible ? chart.filteredData : [];

  const symbols = chart.container
    .select(".spider-chart__points")
    .attr(
      "transform",
      `translate(${chart.innerWidth / 2}, ${chart.innerHeight / 2})`
    )
    .selectAll<SVGElement, DataEntry>(".spider-chart__symbolgroup")
    .data(data, (d) => d.key)
    .join("g")
    .attr("class", "spider-chart__symbolgroup -filled")
    .attr("data-key", (d) => d.key)
    .style("--color", (d) => d.color || "var(--color-text)")
    .selectAll<SVGElement, EntryValue>(".chart__symbol")
    .data(
      (d) => d.values.map((v) => ({ ...v, symbol: d.symbol ?? "circle" })),
      (d) => d.key
    )
    .join(
      (enter) => {
        const symbol = chart
          .appendSymbol(enter)
          .attr(
            "transform",
            (d, i) =>
              `translate(${getCoordX(
                i,
                d.startValue || 0,
                0
              )}, ${getCoordY(i, d.startValue || 0, 0)})`
          );

        return symbol;
      },
      (update) => update,
      (exit) => exit.remove()
    );

  symbols
    .transition()
    .duration(chart.animationDuration)
    .attr(
      "transform",
      (d, i) =>
        `translate(${getCoordX(i, d.value || 0, 0)}, ${getCoordY(
          i,
          d.value || 0,
          0
        )})`
    );

  chart.updateSymbol(symbols);
}

function drawAxes() {
  if (chart.container == null) return;
  if (scale.value == null) return;

  if (!props.settings.ticksVisible) {
    chart.container.selectAll(".spider-chart__axis").remove();
    return;
  }

  if (chart.container.select(".gy").empty()) {
    chart.container.append("g").attr("class", "gy");
  }

  const data: { scale?: number; key: string }[] =
    chart.longestValuesArray.slice();

  while (data.length < axisCount.value) {
    data.push({ scale: 1, key: `extra-line-${data.length}` });
  }

  const domain = scale.value.domain();

  chart.container
    .select(".gy")
    .attr(
      "transform",
      `translate(${chart.innerWidth / 2}, ${chart.innerHeight / 2})`
    )
    .selectAll(".spider-chart__axis")
    .data(data, (d) => (d as { key: string }).key)
    .join("path")
    .attr("class", "spider-chart__axis")
    .transition()
    .duration(chart.animationDuration)
    .attr("d", (d, i) => {
      return line()([
        [getCoordX(i, domain[0]), getCoordY(i, domain[0])],
        [getCoordX(i, domain[1]), getCoordY(i, domain[1])]
      ]);
    });
}

function drawTicks() {
  if (chart.container == null) return;
  if (scale.value == null) return;
  const domain = scale.value.domain();
  const minDomainValue = domain[0];
  const maxDomainValue = domain[1];
  const domainRange = maxDomainValue - minDomainValue;
  if (domainRange == 0) return;

  if (!props.settings.ticksVisible) {
    chart.container.selectAll(".spider-chart__tick").remove();
    return;
  }

  const linePath = line<{ x: number; y: number }>()
    .x(function (d) {
      return d.x;
    })
    .y(function (d) {
      return d.y;
    });

  const ticks = scale.value.ticks(tickAmount.value);

  const tickData = ticks.map((v) => {
    const points: { x: number; y: number }[] = [];
    for (let a = 0; a <= axisCount.value; a++) {
      points.push({
        x: getCoordX(a, v, 0),
        y: getCoordY(a, v, 0)
      });
    }
    return {
      v,
      points
    };
  });

  chart.container
    .select(".gx")
    .attr(
      "transform",
      `translate(${chart.innerWidth / 2}, ${chart.innerHeight / 2})`
    )
    .selectAll<
      SVGPathElement,
      { v: number; points: { x: number; y: number }[] }
    >(".spider-chart__tick")
    .data(tickData, (d) => d.v)
    .join("path")
    .attr("class", "spider-chart__tick")
    .transition()
    .duration(chart.animationDuration)
    .attr("d", (d) => linePath(d.points));
}

function drawLabels() {
  if (chart.container == null) return;
  if (chart.filteredData.length == 0) return;
  if (scale.value == null) return;

  if (!props.settings.xAxisLabelsVisible) {
    chart.container.selectAll(".spider-chart__label").remove();
    return;
  }

  const domain = scale.value.domain();

  const LABEL_Y = domain[1];
  const LABEL_OFFSET = (props.settings.fontSize || 12) + 2;

  const labels = chart.container
    .select(".spider-chart__labels")
    .attr(
      "transform",
      `translate(${chart.innerWidth / 2}, ${chart.innerHeight / 2})`
    )
    .selectAll(".spider-chart__label")
    .data(chart.longestValuesArray, (d) => (d as { key: string }).key)
    .join(
      (enter) =>
        enter
          .append("text")
          .attr("class", "spider-chart__label")
          .attr("x", (d, i) => Math.round(getCoordX(i, LABEL_Y * 1.1)))
          .attr("y", (d, i) => Math.round(getCoordY(i, LABEL_Y * 1.1))),
      (update) => update,
      (exit) => exit.remove()
    )
    .on("dblclick", (_event, d) => {
      emit("click:column-title", {
        uid: d.uid,
        title: d.title,
        shortTitle: d.shortTitle,
        type: d.type,
        id: d.id
      });
    })
    .attr("text-anchor", (d, i) => {
      const x = getCoordX(i, LABEL_Y);
      if (x > 5) {
        return "start";
      } else if (x < -5) {
        return "end";
      } else {
        return "middle";
      }
    })
    .attr("text-anchor-vertical", (d, i) => {
      const y = getCoordY(i, LABEL_Y);
      if (y > 5) {
        return "start";
      } else if (y < -5) {
        return "end";
      } else {
        return "middle";
      }
    })
    .text((d) => (props.settings.xAxisLabelsShortened ? d.shortTitle : d.title));
  
  const fontSize = props.settings.fontSize || 12;

  const labelsTransition = labels
    .transition()
    .duration(chart.animationDuration)
    .ease(easeExpOut)
    .attr("x", (d, i) => Math.round(getCoordX(i, LABEL_Y, LABEL_OFFSET)))
    .attr("y", (d, i) => Math.round(getCoordY(i, LABEL_Y, LABEL_OFFSET)) + 4)
    .style("font-size", `${fontSize}px`);

  wrapText(labelsTransition, 200, chart.measureTextWidthCached, fontSize);

  labels
    .selectAll("title")
    .data((d) => [d])
    .join("title")
    .text((d) => d.title);
}

function drawYAxisLabel() {
  if (!props.settings.yAxisTitleVisible) {
    chart.container?.select(".axis-label--y").remove();
    return;
  }
  const g = chart.container;
  if (!g) return;

  const titleFontSize = props.settings.fontSize || 12;
  // draw axis label
  const yAxisLabel = selectOrCreateChild(g, "g.axis-label--y")
    .attr(
      "transform",
      `translate(${chart.innerWidth / 2 - titleFontSize * 1.4}, ${chart.innerHeight / 2})`
    )
    .on("click", (event) => {
      emit("click:axis-title", {
        axis: "y",
        x: event.clientX,
        y: event.clientY
      });
    });

  const text = props.settings.yAxis
    ? t(`chartAxes.${props.settings.yAxis}`)
    : "";

  // shadow
  selectOrCreateChild(yAxisLabel, "text.axis-label__shadow")
    .attr("x", -10)
    .attr("y", 2)
    .attr("dy", "1em")
    .attr("text-anchor", "end")
    .attr("transform", "rotate(-90)")
    .style("font-size", titleFontSize + "px")
    .attr("stroke", "var(--color-background-deep)")
    .attr("stroke-width", 4)
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .style("opacity", 0.7)
    .text(text);

  // text
  selectOrCreateChild(yAxisLabel, "text.axis-label__text")
    .attr("x", -10)
    .attr("y", 2)
    .attr("dy", "1em")
    .attr("text-anchor", "end")
    .attr("transform", "rotate(-90)")
    .style("font-size", titleFontSize + "px")
    .attr("fill", "var(--color-text)")
    .text(text);
}

function getCoordX(
  axis: number,
  value: number,
  offset = 0
) {
  if (scale.value == null) return 0;
  const angle = (axis / axisCount.value) * 360;
  return (
    -Math.cos(((angle - 90) * Math.PI) / 180) * (scale.value(value) + offset)
  );
}

function getCoordY(
  axis: number,
  value: number,
  offset = 0
) {
  if (scale.value == null) return 0;
  const angle = (axis / axisCount.value) * 360;
  return (
    -Math.sin(((angle - 90) * Math.PI) / 180) * (scale.value(value) + offset)
  );
}
</script>

<style lang="scss">
@use "./chart.scss";

.spider-chart {
  .chart__y-ticks .tick:nth-child(2) {
    opacity: 0;
  }
  &__tick {
    fill: none;
    stroke: #444; // var(--color-text);
    opacity: 0.5;
    mix-blend-mode: difference;
  }
  &__axis {
    fill: none;
    stroke: #444; // var(--color-text);
    opacity: 0.5;
    mix-blend-mode: difference;
  }
  &__area {
    opacity: 0.5;
  }
  &__backgroundarea {
    fill: var(--color-background-deep);
  }
  &__label {
    font-size: 14px;
    font-family: var(--font-family-ui);
    fill: var(--color-text);
  }
}
</style>
